import { useState, useEffect, useReducer } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { get } from 'lodash';

import {
  initialState,
  reducer,
} from '../reducer';
import {
  GET_TIMESHEETS_AWAITING_APPROVAL,
  GET_APPROVED_TIMESHEETS,
  GET_TIMESHEET_BY_ID,
  timesheetsQueryParams,
  timesheetQueryParams,
} from '../graphql/queries';
import {
  TIMESHEETS_FETCH,
  TIMESHEETS_FETCH_SUCCESS,
  TIMESHEET_FETCH_SUCCESS,
  TIMESHEET_REJECT_SUCCESS,
} from '../actionTypes';

const PAGE_SIZE = 25;

const useFetchTimesheetsQuery = ({
  dispatch,
  ownedBy,
  query,
  payloadPath,
  country,
}) => {
  const [timesheetcountry, setTimesheetCountry] = useState(country);
  const [resetStoreOnNextFetchSuccess, setResetStoreOnNextFetchSuccess] = useState(false);
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);

  const [fetchTimesheets, { data, loading, fetchMore: fetchMoreTimesheets }] = useLazyQuery(query, {
    fetchPolicy: 'network-only',
    variables: timesheetsQueryParams({
      ownedBy,
      country,
      first: PAGE_SIZE,
    }),
  });

  const fetch = () => {
    dispatch({ type: TIMESHEETS_FETCH });
    setResetStoreOnNextFetchSuccess(true);

    fetchTimesheets();
  };

  const fetchMore = () => {
    dispatch({ type: TIMESHEETS_FETCH });

    return fetchMoreTimesheets({
      query,
      variables: timesheetsQueryParams({
        ownedBy,
        country,
        first: PAGE_SIZE,
        after: cursor,
      }),
      updateQuery: (_, { fetchMoreResult }) => (fetchMoreResult),
    });
  };

  useEffect(() => {
    if (!loading && data) {
      setHasMore(get(data, `${payloadPath}.pageInfo.hasNextPage`));
      setCursor(get(data, `${payloadPath}.pageInfo.endCursor`));

      const nodes = get(data, `${payloadPath}.nodes`);
      const totalTimesheetsCount = get(data, `${payloadPath}.totalCount`);

      const payload = {
        timesheets: [...nodes],
        totalTimesheetsCount,
      };

      dispatch({
        type: TIMESHEETS_FETCH_SUCCESS,
        payload,
        reset: resetStoreOnNextFetchSuccess || timesheetcountry !== country,
      });
      setResetStoreOnNextFetchSuccess(false);
      setTimesheetCountry(country);
    }
  }, [loading, data]);

  return {
    fetch,
    fetchMore,
    loading: loading && !data,
    hasMore,
  };
};

export const useTimesheetQuery = () => {
  const [store, dispatch] = useReducer(reducer, initialState);
  const [fetch, { data, loading }] = useLazyQuery(GET_TIMESHEET_BY_ID, {
    fetchPolicy: 'network-only',
  });

  const fetchTimesheet = (id) => {
    fetch({ variables: timesheetQueryParams({ id }) });
  };

  useEffect(() => {
    if (!loading && data) {
      const payload = { ...data.weployer };

      dispatch({
        type: TIMESHEET_FETCH_SUCCESS,
        payload,
      });
    }
  }, [data, loading]);

  const { deeplinkedTimesheet } = store;

  return {
    deeplinkedTimesheet,
    fetchTimesheet,
  };
};

export const useSubmittedTimesheetsQuery = ({ ownedBy, country }) => {
  const [store, dispatch] = useReducer(reducer, initialState);

  const {
    fetch,
    fetchMore,
    hasMore,
  } = useFetchTimesheetsQuery({
    dispatch,
    ownedBy,
    country,
    query: GET_TIMESHEETS_AWAITING_APPROVAL,
    payloadPath: 'weployer.timesheetsAwaitingApproval',
  });

  const handleRejectTimesheetSuccess = ({ timesheetId, rejectionReason }) => {
    dispatch({
      type: TIMESHEET_REJECT_SUCCESS,
      payload: {
        timesheetId,
        rejectionReason,
      },
    });
  };

  const {
    loading,
    timesheets,
    orderedTimesheetIds,
    totalTimesheetsCount,
  } = store;

  return {
    timesheets,
    orderedTimesheetIds,
    totalTimesheetsCount,
    fetch,
    fetchMore,
    loading,
    hasMore,
    handleRejectTimesheetSuccess,
  };
};

export const useApprovedTimesheetsQuery = ({ ownedBy }) => {
  const [store, dispatch] = useReducer(reducer, initialState);

  const {
    fetch,
    fetchMore,
    hasMore,
  } = useFetchTimesheetsQuery({
    dispatch,
    ownedBy,
    query: GET_APPROVED_TIMESHEETS,
    payloadPath: 'weployer.approvedTimesheets',
  });

  const {
    loading,
    timesheets,
    orderedTimesheetIds,
    totalTimesheetsCount,
  } = store;

  return {
    timesheets,
    totalTimesheetsCount,
    orderedTimesheetIds,
    fetch,
    fetchMore,
    loading,
    hasMore,
  };
};

export default {
  useTimesheetQuery,
  useSubmittedTimesheetsQuery,
  useApprovedTimesheetsQuery,
};
